import { useMemo } from 'react';
import { MaintenanceRecurrenceDetailModel } from 'api/models';
import Grid from 'components/Grid/Grid';
import { MaintenanceCostDisplay } from 'containercomponents/MaintenanceStatus/MaintenanceCostDisplay/MaintenanceCostDisplay';
import { startCase } from 'es-toolkit/compat';
import { getFormattedFrequency } from 'maintenance/utils';
import MaintenanceTaskModel from 'models/maintenance/MaintenanceTaskModel';
import { MaintenanceRecurrenceAppointments } from 'pages/Accounts/Components/maintenance-recurrence/components/maintenance-recurrence-appointments';
import { MaintenanceRecurrenceHeader } from 'pages/Accounts/Components/maintenance-recurrence/components/maintenance-recurrence-header';
import { MaintenanceRecurrenceNavBar } from 'pages/Accounts/Components/maintenance-recurrence/components/maintenance-recurrence-nav-bar';
import { MaintenanceRecurrenceResidentNotice } from 'pages/Accounts/Components/maintenance-recurrence/components/maintenance-recurrence-resident-notice';
import {
  getDateByStatus,
  getFormattedAddress,
  getRecurrenceAuthorizationNeeded,
} from 'pages/Accounts/Components/maintenance-recurrence/maintenance-recurrence.utils';
import { DATE_FORMATS, formatDate } from 'utils/formatDate';
import { MaintenanceRecurrenceNotes } from './components/maintenance-recurrence-notes';

type MaintenanceRecurrentSubtaskProps = {
  // Both nullable to match the hooks that supply them; the body already reads
  // them through `?.` and gates its markup on `!!masterTask`.
  masterTask: MaintenanceRecurrenceDetailModel | null;
  subtask: MaintenanceTaskModel | null;
  onClosePress: () => void;
  isResident?: boolean;
};

export const MaintenanceRecurrentSubtask = ({
  masterTask,
  subtask,
  onClosePress,
  isResident = false,
}: MaintenanceRecurrentSubtaskProps) => {
  // The subtask being rendered is by construction one of `masterTask`'s own
  // subtasks, so this lookup always resolves.
  const currentRecurringSubtask = masterTask?.subTasks.find(
    (task) => task.id === subtask!.id,
  )!;

  const isResidentAccessRequired = useMemo(
    () =>
      getRecurrenceAuthorizationNeeded(
        masterTask?.maintenanceRecurrence?.recurringTask,
      ),
    [masterTask?.maintenanceRecurrence?.recurringTask],
  );

  const isPricingSectionVisible =
    !isResident || (isResident && subtask?.requestedBy === 'Resident');

  return (
    <div className="flex w-full flex-col items-center">
      <div className="w-full">
        <MaintenanceRecurrenceNavBar
          onClosePress={onClosePress}
          isResident={isResident}
          isSubtask
          task={masterTask}
        />
      </div>
      <div className="px-2sm sm:max-w-[730px] sm:px-0">
        {!!masterTask && (
          <Grid extraPadding={false} className="p-0">
            <div className="mb-2xl">
              <MaintenanceRecurrenceHeader
                title={`${startCase(subtask?.summary || masterTask.maintenanceRecurrence.summary)} - ${formatDate(
                  getDateByStatus(currentRecurringSubtask),
                  DATE_FORMATS.DOTTED_DAY_MEDIUM,
                )}`}
                frequency={
                  masterTask?.maintenanceRecurrence?.repeatFrequencyDays
                    ? getFormattedFrequency(
                        masterTask.maintenanceRecurrence.repeatFrequencyDays,
                      )
                    : undefined
                }
                homeAddress={getFormattedAddress(
                  subtask?.address || masterTask.home.address,
                )}
                masterMaintenanceId={masterTask.maintenanceRecurrence.id}
                maintenanceId={subtask?.id}
                status={currentRecurringSubtask?.subTaskStatus}
              />
            </div>
            {isResident &&
              (masterTask?.maintenanceRecurrence.requestedBy === 'Homeowner' ||
                isResidentAccessRequired) && (
                <div className="mb-2xl">
                  <MaintenanceRecurrenceResidentNotice
                    frequency={getFormattedFrequency(
                      masterTask?.maintenanceRecurrence.repeatFrequencyDays,
                    )}
                    isAccessRequired={isResidentAccessRequired}
                    isRequestedByHomeowner={
                      masterTask?.maintenanceRecurrence.requestedBy ===
                      'Homeowner'
                    }
                    conciergeName={masterTask?.concierge?.firstName}
                    homeownerName={masterTask.homeowner?.firstName}
                    isSubtask
                  />
                </div>
              )}

            {!!subtask && (
              <MaintenanceRecurrenceNotes
                // @ts-expect-error -- Legacy typecheck baseline; the `MaintenanceTaskModel` domain class carries a subset of the generated `MaintenanceModel`. See docs/development/typecheck-baseline-followups.md. Refs: ARCH-56
                subtask={subtask}
                isResident={isResident}
              />
            )}

            <MaintenanceRecurrenceAppointments
              appointments={subtask?.appointments}
              disabled={!subtask}
            />

            {isPricingSectionVisible && (
              <MaintenanceCostDisplay
                task={subtask?.actualCost || subtask?.estimatedCost}
                actualCost={!!subtask?.actualCost}
                // @ts-expect-error -- Legacy typecheck baseline; the `MaintenanceTaskModel` domain class carries a subset of the generated `MaintenanceModel`. See docs/development/typecheck-baseline-followups.md. Refs: ARCH-56
                fullTask={subtask || masterTask}
                isRenter={isResident}
                disabled={
                  !subtask || (!subtask.actualCost && !subtask.estimatedCost)
                }
                customClassName="m-0 p-0"
                defaultClassName="block"
                hideBackgroundIcon
              />
            )}
          </Grid>
        )}
      </div>
    </div>
  );
};
